import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { UserPlus, ArrowLeft, Check, X, Baby, Plus } from "lucide-react";
import { DependentFields, EMPTY_DEPENDENT } from "../components/DependentsPanel";
import { format, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const situacaoOptions = [
  "Sou Membro da Life",
  "Aceitei Jesus",
  "Reconciliei",
  "Sou cristão e quero me unir à Life",
  "Sou cristão mas ainda não sou parte da Life",
  "Frequento eventualmente"
];

const batismoOptions = [
  "Já sou batizado",
  "Desejo me batizar",
  "Nenhuma das opções"
];

const estadoCivilOptions = ["Casado(a)", "Solteiro(a)", "Viúvo(a)", "Divorciado(a)", "Amasiado(a)"];

const initialFormState = {
  nome_completo: "",
  email: "",
  telefone: "",
  endereco: "",
  cidade: "",
  sexo: "",
  data_nascimento: "",
  estado_civil: "",
  situacao: "",
  batismo: "",
  ministerios_desejados: [],
  funcoes_desejadas: [],
  campos_personalizados: {}
};

export default function PublicRegistrationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filialId = searchParams.get("unidade");
  
  const [ministries, setMinistries] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [success, setSuccess] = useState(false);
  const [dependentes, setDependentes] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [formConfig, setFormConfig] = useState(null);

  useEffect(() => {
    loadData();
  }, [filialId]);

  const loadData = async () => {
    setLoadingConfig(true);
    try {
      // Pass filialId to filter ministries and functions by branch
      const params = filialId ? `?filial_id=${filialId}` : '';
      const [ministriesRes, functionsRes] = await Promise.all([
        axios.get(`${API}/ministries/public${params}`),
        axios.get(`${API}/functions/public${params}`)
      ]);
      setMinistries(ministriesRes.data);
      setFunctions(functionsRes.data);

      // Load form config if filialId is provided
      if (filialId) {
        try {
          const configRes = await axios.get(`${API}/public-form-config/${filialId}`);
          setFormConfig(configRes.data);
          setForm(prev => ({ ...prev, filial_id: filialId }));
        } catch (error) {
          console.log("No custom config, using defaults");
        }
      }
    } catch (error) {
      // Silently fail - public route
    } finally {
      setLoadingConfig(false);
    }
  };

  const getFieldConfig = (fieldKey) => {
    if (!formConfig?.campos_padrao?.[fieldKey]) {
      // Campos opcionais por padrão: ministerio_desejado, endereco, data_nascimento
      const optionalFields = ["ministerio_desejado", "endereco", "data_nascimento"];
      return { visivel: true, obrigatorio: !optionalFields.includes(fieldKey) };
    }
    return formConfig.campos_padrao[fieldKey];
  };

  const getCityOptions = () => {
    return formConfig?.cidades_opcoes || ["Senador Canedo", "Goiânia", "Aparecida de Goiânia", "Outros"];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const requiredFields = [
      { key: "cidade", label: "cidade" },
      { key: "sexo", label: "sexo" },
      { key: "estado_civil", label: "estado civil" },
      { key: "situacao", label: "situação" },
      { key: "batismo", label: "opção de batismo" }
    ];

    for (const field of requiredFields) {
      const config = getFieldConfig(field.key);
      if (config.visivel && config.obrigatorio && !form[field.key]) {
        toast.error(`Por favor, selecione ${field.label}`);
        return;
      }
    }

    if (formConfig?.campos_personalizados) {
      for (const customField of formConfig.campos_personalizados) {
        if (customField.obrigatorio && !form.campos_personalizados?.[customField.id]) {
          toast.error(`Por favor, preencha ${customField.label}`);
          return;
        }
      }
    }
    
    setLoading(true);

    try {
      const deps = dependentes.filter(d => d.nome_completo?.trim() && d.data_nascimento);
      if (deps.length !== dependentes.length) {
        toast.error("Preencha nome e data de nascimento de cada dependente ou remova-o");
        setLoading(false);
        return;
      }
      await axios.post(`${API}/people/public`, { ...form, dependentes: deps.map(d => ({ ...d, sexo: d.sexo || null })) });
      setSuccess(true);
      toast.success("Cadastro realizado com sucesso!");
      setTimeout(() => navigate("/news"), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao realizar cadastro");
    } finally {
      setLoading(false);
    }
  };

  const toggleMinistry = (ministryId) => {
    const isSelected = form.ministerios_desejados.includes(ministryId);
    if (isSelected) {
      setForm({
        ...form,
        ministerios_desejados: form.ministerios_desejados.filter(id => id !== ministryId)
      });
    } else {
      setForm({
        ...form,
        ministerios_desejados: [...form.ministerios_desejados, ministryId]
      });
    }
  };

  const toggleFunction = (functionId) => {
    const isSelected = form.funcoes_desejadas.includes(functionId);
    if (isSelected) {
      setForm({
        ...form,
        funcoes_desejadas: form.funcoes_desejadas.filter(id => id !== functionId)
      });
    } else {
      setForm({
        ...form,
        funcoes_desejadas: [...form.funcoes_desejadas, functionId]
      });
    }
  };

  const getAvailableFunctions = () => {
    if (form.ministerios_desejados.length === 0) return [];
    return functions.filter(f => 
      f.ministerios_ids?.some(mid => form.ministerios_desejados.includes(mid))
    );
  };

  if (success) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Cadastro Realizado!</h2>
            <p className="text-muted-foreground mb-6">
              Seus dados foram enviados com sucesso. Em breve entraremos em contato.
            </p>
            <p className="text-sm text-muted-foreground">Redirecionando...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 p-4 pb-8">
      <div className="max-w-2xl mx-auto">
        <Button 
          variant="ghost" 
          className="mb-4 gap-2"
          onClick={() => navigate("/login")}
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar ao Login
        </Button>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <UserPlus className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">
              {formConfig?.nome_formulario || "Cadastro"}
            </CardTitle>
            <CardDescription>
              {formConfig?.filial_nome && `${formConfig.filial_nome} - `}
              Preencha seus dados para fazer parte da nossa comunidade
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingConfig ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nome Completo */}
              {getFieldConfig("nome_completo").visivel && (
              <div className="space-y-2">
                <Label>Nome Completo {getFieldConfig("nome_completo").obrigatorio && "*"}</Label>
                <Input
                  required={getFieldConfig("nome_completo").obrigatorio}
                  value={form.nome_completo}
                  onChange={(e) => setForm({ ...form, nome_completo: e.target.value })}
                  data-testid="public-nome"
                />
              </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getFieldConfig("email").visivel && (
                <div className="space-y-2">
                  <Label>Email {getFieldConfig("email").obrigatorio && "*"}</Label>
                  <Input
                    type="email"
                    required={getFieldConfig("email").obrigatorio}
                    value={form.email || ""}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="public-email"
                  />
                </div>
                )}
                {getFieldConfig("telefone").visivel && (
                <div className="space-y-2">
                  <Label>Telefone {getFieldConfig("telefone").obrigatorio && "*"}</Label>
                  <Input
                    required={getFieldConfig("telefone").obrigatorio}
                    placeholder="(00) 00000-0000"
                    value={form.telefone || ""}
                    onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                    data-testid="public-telefone"
                  />
                </div>
                )}
              </div>

              {getFieldConfig("endereco").visivel && (
              <div className="space-y-2">
                <Label>Endereço {getFieldConfig("endereco").obrigatorio && "*"}</Label>
                <Input
                  required={getFieldConfig("endereco").obrigatorio}
                  value={form.endereco || ""}
                  onChange={(e) => setForm({ ...form, endereco: e.target.value })}
                  data-testid="public-endereco"
                />
              </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getFieldConfig("cidade").visivel && (
                <div className="space-y-2">
                  <Label>Cidade {getFieldConfig("cidade").obrigatorio && "*"}</Label>
                  <Select
                    value={form.cidade || ""}
                    onValueChange={(v) => setForm({ ...form, cidade: v })}
                  >
                    <SelectTrigger data-testid="public-cidade">
                      <SelectValue placeholder="Selecione a cidade" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-[200px]">
                      {getCityOptions().map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
                {getFieldConfig("sexo").visivel && (
                <div className="space-y-2">
                  <Label>Sexo {getFieldConfig("sexo").obrigatorio && "*"}</Label>
                  <Select
                    value={form.sexo || ""}
                    onValueChange={(v) => setForm({ ...form, sexo: v })}
                  >
                    <SelectTrigger data-testid="public-sexo">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-[200px]">
                      <SelectItem value="masculino">Masculino</SelectItem>
                      <SelectItem value="feminino">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getFieldConfig("data_nascimento").visivel && (
                <div className="space-y-2">
                  <Label>Data de Nascimento {getFieldConfig("data_nascimento").obrigatorio && "*"}</Label>
                  <Input
                    type="date"
                    required={getFieldConfig("data_nascimento").obrigatorio}
                    value={form.data_nascimento || ""}
                    onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })}
                    data-testid="public-nascimento"
                  />
                </div>
                )}
                {getFieldConfig("estado_civil").visivel && (
                <div className="space-y-2">
                  <Label>Estado Civil {getFieldConfig("estado_civil").obrigatorio && "*"}</Label>
                  <Select
                    value={form.estado_civil || ""}
                    onValueChange={(v) => setForm({ ...form, estado_civil: v })}
                  >
                    <SelectTrigger data-testid="public-estado-civil">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-[200px]">
                      {estadoCivilOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
              </div>

              {getFieldConfig("situacao").visivel && (
              <div className="space-y-2">
                <Label>Qual é a sua situação? {getFieldConfig("situacao").obrigatorio && "*"}</Label>
                <Select
                  value={form.situacao || ""}
                  onValueChange={(v) => setForm({ ...form, situacao: v })}
                >
                  <SelectTrigger data-testid="public-situacao">
                    <SelectValue placeholder="Selecione sua situação" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[200px]">
                    {situacaoOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}

              {getFieldConfig("batismo").visivel && (
              <div className="space-y-2">
                <Label>Batismo {getFieldConfig("batismo").obrigatorio && "*"}</Label>
                <Select
                  value={form.batismo || ""}
                  onValueChange={(v) => setForm({ ...form, batismo: v })}
                >
                  <SelectTrigger data-testid="public-batismo">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[200px]">
                    {batismoOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}

              {/* Custom Fields */}
              {formConfig?.campos_personalizados?.length > 0 && (
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-medium text-sm text-muted-foreground">Informações Adicionais</h3>
                  {formConfig.campos_personalizados.map((customField) => (
                    <div key={customField.id} className="space-y-2">
                      <Label>{customField.label} {customField.obrigatorio && "*"}</Label>
                      {customField.tipo === "texto" && (
                        <Input
                          required={customField.obrigatorio}
                          value={form.campos_personalizados?.[customField.id] || ""}
                          onChange={(e) => setForm({
                            ...form,
                            campos_personalizados: {
                              ...form.campos_personalizados,
                              [customField.id]: e.target.value
                            }
                          })}
                        />
                      )}
                      {customField.tipo === "numero" && (
                        <Input
                          type="number"
                          required={customField.obrigatorio}
                          value={form.campos_personalizados?.[customField.id] || ""}
                          onChange={(e) => setForm({
                            ...form,
                            campos_personalizados: {
                              ...form.campos_personalizados,
                              [customField.id]: e.target.value
                            }
                          })}
                        />
                      )}
                      {customField.tipo === "data" && (
                        <Input
                          type="date"
                          required={customField.obrigatorio}
                          value={form.campos_personalizados?.[customField.id] || ""}
                          onChange={(e) => setForm({
                            ...form,
                            campos_personalizados: {
                              ...form.campos_personalizados,
                              [customField.id]: e.target.value
                            }
                          })}
                        />
                      )}
                      {customField.tipo === "selecao" && (
                        <Select
                          value={form.campos_personalizados?.[customField.id] || ""}
                          onValueChange={(v) => setForm({
                            ...form,
                            campos_personalizados: {
                              ...form.campos_personalizados,
                              [customField.id]: v
                            }
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent position="popper" className="max-h-[200px]">
                            {customField.opcoes?.map((opt) => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {customField.tipo === "checkbox" && (
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={form.campos_personalizados?.[customField.id] === true}
                            onCheckedChange={(checked) => setForm({
                              ...form,
                              campos_personalizados: {
                                ...form.campos_personalizados,
                                [customField.id]: checked
                              }
                            })}
                          />
                          <span className="text-sm">Sim</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Múltipla seleção de Ministérios */}
              {getFieldConfig("ministerio_desejado").visivel && (
              <div className="space-y-2">
                <Label>Deseja servir em algum ministério?</Label>
                <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
                  <div className="flex flex-wrap gap-2">
                    {ministries.map((m) => {
                      const isSelected = form.ministerios_desejados.includes(m.id);
                      return (
                        <Badge
                          key={m.id}
                          variant={isSelected ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleMinistry(m.id)}
                          data-testid={`public-ministry-${m.id}`}
                        >
                          {m.nome}
                          {isSelected && <X className="h-3 w-3 ml-1" />}
                        </Badge>
                      );
                    })}
                  </div>
                  {form.ministerios_desejados.length === 0 && (
                    <p className="text-xs text-muted-foreground">Clique para selecionar ministérios (opcional)</p>
                  )}
                </div>
              </div>
              )}

              {/* Múltipla seleção de Funções */}
              {form.ministerios_desejados.length > 0 && (
                <div className="space-y-2">
                  <Label>Qual função gostaria de exercer?</Label>
                  <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
                    <div className="flex flex-wrap gap-2">
                      {getAvailableFunctions().map((f) => {
                        const isSelected = form.funcoes_desejadas.includes(f.id);
                        return (
                          <Badge
                            key={f.id}
                            variant={isSelected ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => toggleFunction(f.id)}
                            data-testid={`public-function-${f.id}`}
                          >
                            {f.nome}
                            {isSelected && <X className="h-3 w-3 ml-1" />}
                          </Badge>
                        );
                      })}
                      {getAvailableFunctions().length === 0 && (
                        <p className="text-xs text-muted-foreground">Nenhuma função disponível para os ministérios selecionados</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Dependentes (crianças) do cadastrado */}
              <div className="space-y-3 p-4 rounded-xl border border-sky-100 bg-sky-50/50" data-testid="public-dependents">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-medium flex items-center gap-1.5"><Baby className="h-4 w-4 text-sky-600" /> Tem filhos? Cadastre seus dependentes</p>
                    <p className="text-xs text-muted-foreground">Crianças até 11 anos entram automaticamente no Ministério Infantil</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" className="h-8 gap-1 shrink-0" onClick={() => setDependentes([...dependentes, { ...EMPTY_DEPENDENT }])} data-testid="public-add-dependent">
                    <Plus className="h-3.5 w-3.5" /> Adicionar
                  </Button>
                </div>
                {dependentes.map((dep, i) => (
                  <div key={i} className="p-3 rounded-lg border bg-background space-y-2" data-testid={`public-dependent-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Dependente {i + 1}</p>
                      <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-destructive gap-1" onClick={() => setDependentes(dependentes.filter((_, j) => j !== i))} data-testid={`public-remove-dependent-${i}`}>
                        <X className="h-3.5 w-3.5" /> Remover
                      </Button>
                    </div>
                    <DependentFields form={dep} setForm={(f) => setDependentes(dependentes.map((d, j) => (j === i ? f : d)))} responsavelTelefone={form.telefone} idPrefix={`public-dep-${i}`} />
                  </div>
                ))}
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold gap-2 mt-6"
                disabled={loading}
                data-testid="public-submit"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <UserPlus className="h-5 w-5" />
                    Enviar Cadastro
                  </>
                )}
              </Button>
            </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
